import { useMutation, useQueryClient } from "react-query";

import { updateGoalStatus } from "api/goals";
import { useGlobalToast } from "components/GlobalToastProvider";
import { GET_GOAL_BY_ID, GET_LIST_OF_GOALS } from "@constants/reactQueryKeys";

interface DiscontinueGoalPayload {
  goalId: number;
  clientId: string;
  rationale: string;
}

/**
 * Custom hook to discontinue a goal.
 *
 * @param options - Optional configuration for success and error handling.
 * @returns An object containing the discontinueGoal function, loading state, and other mutation info.
 */
const useDiscontinueGoal = (options: any = {}) => {
  const { onSuccess = () => {}, onError = () => {}, ...otherOptions } = options;
  const queryClient = useQueryClient();
  const { showToast } = useGlobalToast();

  const {
    mutate: discontinueGoal,
    isLoading: isDiscontinuingGoal,
    ...rest
  } = useMutation(
    ({ goalId, rationale }: DiscontinueGoalPayload) =>
      updateGoalStatus(goalId, { new_status: "discontinued", rationale }),
    {
      onSuccess: (response, variables) => {
        showToast({ message: "Goal discontinued" });

        queryClient.invalidateQueries([GET_LIST_OF_GOALS, variables.clientId]);
        queryClient.invalidateQueries([GET_GOAL_BY_ID, variables.goalId]);

        onSuccess(response, variables);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.error || "Failed to discontinue goal";
        showToast({ message: errorMessage, errorState: true });

        onError(error);
      },
      ...otherOptions,
    }
  );

  return {
    discontinueGoal,
    isDiscontinuingGoal,
    ...rest,
  };
};

export default useDiscontinueGoal;
